import { supabase } from '../config/supabase'
import { getCancellationPolicy } from './cancellationPolicyService'
import { getReschedulePolicy } from './reschedulePolicyService'
import { getTaxPolicy } from './taxService'

/**
 * Record a payment movement in appointment_payments table
 * This creates an audit trail of all charges and refunds with policy snapshots
 * 
 * @param {Object} params
 * @param {string} params.appointmentId - Appointment ID
 * @param {string} params.userId - User ID
 * @param {string} params.paymentType - 'booking_charge' | 'reschedule_fee' | 'cancellation_fee' | 'refund'
 * @param {string} params.direction - 'charge' | 'refund'
 * @param {number} params.amountUSD - Amount in USD
 * @param {string} [params.paymentIntentId] - Stripe Payment Intent ID (for charges)
 * @param {string} [params.refundId] - Stripe Refund ID (for refunds)
 * @param {Object} [params.meta] - Additional metadata (fee_percentage, reason, old_slot, new_slot, etc.)
 * @returns {Promise<{success: boolean, error?: string, data?: Object}>}
 */
export const recordAppointmentPayment = async ({
  appointmentId,
  userId,
  paymentType,
  direction,
  amountUSD,
  paymentIntentId = null,
  refundId = null,
  meta = null,
}) => {
  try {
    const { isDemoMode } = await import('../utils/demoMode')
    if (isDemoMode()) {
      console.log('🎭 DEMO MODE — skipping appointment_payments insert')
      return { success: true, data: { id: `demo-pay-${Date.now()}`, _demo: true } }
    }

    // Validate required fields
    if (!appointmentId || !userId || !paymentType || !direction || amountUSD == null) {
      throw new Error('Missing required fields: appointmentId, userId, paymentType, direction, amountUSD')
    }

    // Validate paymentType
    const validPaymentTypes = ['booking_charge', 'reschedule_fee', 'cancellation_fee', 'refund']
    if (!validPaymentTypes.includes(paymentType)) {
      throw new Error(`Invalid paymentType: ${paymentType}. Must be one of: ${validPaymentTypes.join(', ')}`)
    }

    // Validate direction
    if (!['charge', 'refund'].includes(direction)) {
      throw new Error(`Invalid direction: ${direction}. Must be 'charge' or 'refund'`)
    }

    // Fetch current policies to create snapshot
    console.log('📸 Fetching policy snapshot for payment record...')
    const [cancellationPolicy, reschedulePolicy, taxPolicy] = await Promise.all([
      getCancellationPolicy(),
      getReschedulePolicy(),
      getTaxPolicy(),
    ])

    // Build policy snapshot (store raw DB values, not transformed)
    const policySnapshot = {
      cancellation_policy: cancellationPolicy ? {
        fee_policy_enabled: cancellationPolicy.feePolicyEnabled || false,
        fee_window_hours: cancellationPolicy.feeWindowHours,
        fee_percentage: cancellationPolicy.feePercentage,
        cutoff_policy_enabled: cancellationPolicy.cutoffPolicyEnabled || false,
        cutoff_hours_before: cancellationPolicy.cutoffHoursBefore,
      } : null,
      reschedule_policy: reschedulePolicy ? {
        fee_policy_enabled: reschedulePolicy.feePolicyEnabled || false,
        fee_window_hours: reschedulePolicy.feeWindowHours,
        fee_percentage: reschedulePolicy.feePercentage,
        cutoff_policy_enabled: reschedulePolicy.cutoffPolicyEnabled || false,
        cutoff_hours_before: reschedulePolicy.cutoffHoursBefore,
        max_reschedules_enabled: reschedulePolicy.maxReschedulesEnabled || false,
        max_reschedules: reschedulePolicy.maxReschedules,
      } : null,
      tax_policy: taxPolicy ? {
        tax_enabled: taxPolicy.enabled || false,
        tax_percentage: taxPolicy.percentage || 0,
      } : null,
    }

    // Insert payment record
    const { data, error } = await supabase
      .from('appointment_payments')
      .insert({
        appointment_id: appointmentId,
        user_id: userId,
        payment_type: paymentType,
        direction: direction,
        amount_usd: Number(amountUSD),
        currency: 'usd',
        payment_intent_id: paymentIntentId,
        refund_id: refundId,
        policy_snapshot: policySnapshot,
        meta: meta,
      })
      .select()
      .single()

    if (error) {
      console.error('❌ Error recording appointment payment:', error)
      throw error
    }

    console.log('✅ Payment record created:', data.id)
    return { success: true, data }
  } catch (error) {
    console.error('❌ Exception in recordAppointmentPayment:', error)
    return {
      success: false,
      error: error.message || 'Failed to record payment',
    }
  }
}

export const collectAppointmentIdsForFeeLookup = (appointment) => {
  const ids = new Set()
  const id = appointment?.id || appointment?.originalData?.id
  if (id) ids.add(id)
  const notes = appointment?.notes || appointment?.originalData?.notes || ''
  const m = notes.match(/\[RESCHEDULED_FROM:([0-9a-f-]{36})\]/i)
  if (m && m[1]) ids.add(m[1])
  return [...ids]
}

export const fetchAppointmentPolicyFeeCharges = async (appointmentId) => {
  if (!appointmentId) {
    return { success: true, rows: [] }
  }
  try {
    const { data, error } = await supabase
      .from('appointment_payments')
      .select('id, payment_type, direction, amount_usd, created_at, meta')
      .eq('appointment_id', appointmentId)
      .eq('direction', 'charge')
      .in('payment_type', ['reschedule_fee', 'cancellation_fee'])
      .order('created_at', { ascending: true })

    if (error) {
      return { success: false, error: error.message, rows: [] }
    }
    return { success: true, rows: data || [] }
  } catch (e) {
    return { success: false, error: e?.message || 'Failed to load fees', rows: [] }
  }
}

export const fetchAllPolicyFeeChargesForAppointment = async (appointment) => {
  const ids = collectAppointmentIdsForFeeLookup(appointment).filter(Boolean)
  if (ids.length === 0) {
    return { success: true, rows: [] }
  }
  try {
    const { data, error } = await supabase
      .from('appointment_payments')
      .select('id, payment_type, direction, amount_usd, created_at, meta')
      .in('appointment_id', ids)
      .eq('direction', 'charge')
      .in('payment_type', ['reschedule_fee', 'cancellation_fee'])
      .order('created_at', { ascending: true })

    if (error) {
      return { success: false, error: error.message, rows: [] }
    }
    const seen = new Set()
    const rows = []
    for (const row of data || []) {
      if (row?.id && !seen.has(row.id)) {
        seen.add(row.id)
        rows.push(row)
      }
    }
    return { success: true, rows }
  } catch (e) {
    return { success: false, error: e?.message || 'Failed to load fees', rows: [] }
  }
}

export const parseRefundAmountFromNotes = (notes) => {
  if (!notes || typeof notes !== 'string') return 0
  const re = /\[REFUND_PROCESSED:[^:]+:([\d.]+)USD/gi
  let total = 0
  let match
  while ((match = re.exec(notes)) !== null) {
    total += parseFloat(match[1]) || 0
  }
  return total
}

/** Sum recorded refund rows for this appointment (and parent if rescheduled). */
export const fetchAppointmentRefundTotal = async (appointment) => {
  const ids = collectAppointmentIdsForFeeLookup(appointment).filter(Boolean)
  const notes = appointment?.notes || appointment?.originalData?.notes || ''
  const fromNotes = parseRefundAmountFromNotes(notes)

  if (ids.length === 0) {
    return { success: true, amountUsd: fromNotes }
  }

  try {
    const { data, error } = await supabase
      .from('appointment_payments')
      .select('id, amount_usd, direction, payment_type, meta')
      .in('appointment_id', ids)
      .eq('direction', 'refund')

    if (error) {
      return { success: false, error: error.message, amountUsd: fromNotes }
    }

    const seen = new Set()
    let fromDb = 0
    for (const row of data || []) {
      if (row?.id && seen.has(row.id)) continue
      if (row?.id) seen.add(row.id)
      fromDb += Number(row.amount_usd) || 0
    }

    return { success: true, amountUsd: fromDb > 0 ? fromDb : fromNotes }
  } catch (e) {
    return { success: false, error: e?.message || 'Failed to load refund', amountUsd: fromNotes }
  }
}
